import React, { useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { FiInfo, FiRotateCcw } from "react-icons/fi";
import {
  Section, Widget, WidgetGrid, tokens,
} from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { Notice, PeriodStats, Tag, TopHeadline } from "../../creditos/styles";
import { Capsule } from "../../creditos/components/ControlBar";
import { formatCompact, formatCurrency, formatPercent } from "../../creditos/dashboardData";
import { hairline, surface, tokens as T } from "../../../styles/designTokens";
import { PlotHead, PlotRow, directionColor, signed } from "../dashboard/ReformaDashboard";
import { DEFAULT_PARAMS, PARAM_META, useReformaParams } from "../hooks/useReformaParams";
import MarginFlow from "./MarginFlow";
import { buildSimulation } from "./simulationData";
import { AnimatedMoney as MoneyValue, FluidRange, Num } from "../components/Motion";
import { finePercent } from "../components/Motion";

/**
 * Aba Simulação: como a carga é calculada e o que muda ao mexer em uma premissa.
 * Elementos próprios: fluxo da receita, simulador ao vivo, extrato com a cobertura do
 * débito pelo crédito e sensibilidade por premissa. Os controles usam os mesmos
 * parâmetros da aba Parâmetros, então as outras abas acompanham.
 */

const dot = (color) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, opacity: 1 }} />;
const label2 = ({ theme }) => tokens.label2(theme);

// Faixa de cada controle, nos limites que fazem sentido para a premissa
export const RANGES = {
  cbsPlena2033: { min: 0, max: 15, step: 0.1 },
  ibsSpPlena2033: { min: 0, max: 30, step: 0.1 },
  pctReceitaVeiculos: { min: 0, max: 100, step: 1 },
  pctCogsVeiculos: { min: 50, max: 100, step: 0.5 },
  pctCogsPecasServ: { min: 30, max: 100, step: 0.5 },
  issAtualServicos: { min: 2, max: 5, step: 0.5 },
};

const SHORT_LABELS = {
  cbsPlena2033: "CBS plena",
  ibsSpPlena2033: "IBS SP pleno",
  pctReceitaVeiculos: "Receita em produtos com ST",
  pctCogsVeiculos: "COGS de produtos com ST",
  pctCogsPecasServ: "COGS de demais e serviços",
  issAtualServicos: "ISS atual",
};

const Legend = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 0.76em;
`;

export const Control = styled.label`
  display: block;
  padding: 11px 0;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  cursor: pointer;

  .top { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; font-size: 0.82em; }
  .name { font-weight: 500; letter-spacing: -0.01em; }
  .val { font-weight: 600; letter-spacing: -0.02em; }
  .val.changed { color: ${T.brand}; }

  .slider { position: relative; margin-top: 9px; height: 18px; }
  .mark { position: absolute; top: 5px; width: 2px; height: 8px; margin-left: -1px; border-radius: 1px; background: ${({ theme }) => tokens.label3(theme)}; }

  input[type="range"] {
    position: absolute;
    inset: 0;
    width: 100%;
    margin: 0;
    height: 18px;
    background: transparent;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
  }
  input[type="range"]:focus-visible { outline: none; }
  input[type="range"]:focus-visible::-webkit-slider-thumb { box-shadow: 0 0 0 3px ${T.brandRing}; }
  input[type="range"]:focus-visible::-moz-range-thumb { box-shadow: 0 0 0 3px ${T.brandRing}; }

  input[type="range"]::-webkit-slider-runnable-track {
    height: 3px;
    border-radius: 2px;
    background: linear-gradient(to right, var(--fill) var(--p), var(--rest) var(--p));
  }
  input[type="range"]::-moz-range-track { height: 3px; border-radius: 2px; background: var(--rest); }
  input[type="range"]::-moz-range-progress { height: 3px; border-radius: 2px; background: var(--fill); }
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    margin-top: -6.5px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35), 0 0 0 0.5px rgba(0, 0, 0, 0.1);
    transition: transform 150ms cubic-bezier(0.3, 1.4, 0.5, 1), box-shadow 150ms ease;
  }
  input[type="range"].dragging::-webkit-slider-thumb { transform: scale(1.22); box-shadow: 0 3px 12px rgba(0, 0, 0, 0.42), 0 0 0 0.5px rgba(0, 0, 0, 0.1); }
  input[type="range"].dragging::-moz-range-thumb { transform: scale(1.22); }

  /* Valor sobre o polegar, durante o arraste */
  .bubble {
    position: absolute;
    bottom: 23px;
    z-index: 2;
    padding: 2px 7px;
    border-radius: 7px;
    font-size: 0.74em;
    font-weight: 650;
    letter-spacing: -0.02em;
    white-space: nowrap;
    pointer-events: none;
    color: ${({ theme }) => (tokens.dk(theme) ? "#1c1c1e" : "#f5f5f7")};
    background: ${({ theme }) => (tokens.dk(theme) ? "#f5f5f7" : "#1c1c1e")};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    opacity: 0;
    transform: translate(-50%, 4px) scale(0.92);
    transition: opacity 140ms ease, transform 180ms cubic-bezier(0.3, 1.4, 0.5, 1);
  }
  .bubble.on { opacity: 1; transform: translate(-50%, 0) scale(1); }
  @media (prefers-reduced-motion: reduce) {
    .bubble, input[type="range"]::-webkit-slider-thumb { transition: none; }
  }
  input[type="range"]::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: none;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  }
`;

export const ThinNote = styled.p`
  margin: 10px 0 0;
  font-size: 0.74em;
  font-weight: 300;
  line-height: 1.45;
  color: ${label2};
`;

const Scenario = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;

  .n { font-size: 1.2em; font-weight: 600; letter-spacing: -0.03em; line-height: 1.1; white-space: nowrap; }
  .l { margin-top: 3px; font-size: 0.72em; color: ${label2}; }
`;

/* Tributo, cobertura do débito, e os três valores em colunas fixas */
const REGIME_COLUMNS = "minmax(104px, 1.2fr) minmax(44px, 0.8fr) 82px 82px 88px";

const RegimeHead = styled.div`
  display: grid;
  grid-template-columns: ${REGIME_COLUMNS};
  gap: 10px;
  margin-top: 14px;
  padding-bottom: 7px;
  font-size: 0.7em;
  color: ${({ theme }) => tokens.label3(theme)};
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  > :nth-child(n + 3) { text-align: right; }
`;

const RegimeRow = styled.div`
  display: grid;
  grid-template-columns: ${REGIME_COLUMNS};
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 7px 0;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};

  &:last-child { border-bottom: none; }
  .name { min-width: 0; font-size: 0.86em; font-weight: 500; letter-spacing: -0.012em; }
  .name small { display: block; margin-top: 2px; font-size: 0.84em; font-weight: 300; letter-spacing: 0; line-height: 1.3; color: ${label2}; }
  .bar { display: flex; height: 5px; border-radius: 3px; overflow: hidden; gap: 1px; background: ${({ theme }) => surface(theme, 2)}; }
  .bar span { display: block; min-width: 0; }
  .n { text-align: right; white-space: nowrap; font-size: 0.82em; color: ${label2}; }
  .net { text-align: right; white-space: nowrap; font-size: 0.94em; font-weight: 600; letter-spacing: -0.025em; }

  &.muted { min-height: 38px; }
  &.muted .name { font-weight: 400; color: ${label2}; font-size: 0.8em; grid-column: 1 / 3; }
`;

const RegimeBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  > * { flex: 1 1 auto; }
`;

const TornadoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 32px;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;

function SliderControl({ meta, value, onChange }) {
  const range = RANGES[meta.id];
  const reference = DEFAULT_PARAMS[meta.id];
  const numeric = Number(value) || 0;
  const position = (v) => ((v - range.min) / (range.max - range.min)) * 100;
  const changed = numeric !== reference;
  const theme = useTheme();
  return (
    <Control style={{ "--p": `${Math.max(0, Math.min(100, position(numeric)))}%` }}>
      <span className="top">
        <span className="name">{SHORT_LABELS[meta.id]}</span>
        <span className={`val${changed ? " changed" : ""}`}><Num value={numeric} format={finePercent} /></span>
      </span>
      <span className="slider" style={{ display: "block", "--fill": changed ? T.brand : tokens.label2(theme), "--rest": "rgba(118,118,128,0.28)" }}>
        <span className="mark" style={{ left: `${position(reference)}%` }} title={`Referência: ${formatPercent(reference)}`} />
        <FluidRange
          min={range.min}
          max={range.max}
          step={range.step}
          value={numeric}
          format={finePercent}
          aria-label={meta.label}
          aria-valuetext={finePercent(numeric)}
          onChange={(v) => onChange(meta.id, v)}
        />
      </span>
    </Control>
  );
}

function SimulatorPanel({ params, setParam, resetParams, impact, isIndustrial }) {
  const changed = PARAM_META.some((m) => Number(params[m.id]) !== DEFAULT_PARAMS[m.id]);
  return (
    <Widget $delay={200} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Simulador</div>
          <div className="w-sub">Arraste, e a tela recalcula</div>
        </div>
        {changed && <Capsule type="button" onClick={resetParams} title="Voltar às premissas de referência" style={{ height: 28, padding: "0 10px" }}><FiRotateCcw /> Restaurar</Capsule>}
      </div>
      <div style={{ marginTop: 12 }}>
        {PARAM_META.map((m) => <SliderControl key={m.id} meta={m} value={params[m.id]} onChange={setParam} />)}
      </div>
      <Scenario style={{ marginTop: "auto" }}>
        <div>
          <div className="n" style={{ color: directionColor(impact.value) }} title={formatCurrency(impact.value)}><Num value={impact.value} format={signed} /></div>
          <div className="l">Impacto neste cenário</div>
        </div>
        {changed && (
          <div style={{ textAlign: "right" }}>
            <div className="n" style={{ fontSize: "0.98em" }} title={formatCurrency(impact.fromReference)}><Num value={impact.fromReference} format={signed} /></div>
            <div className="l">sobre a referência</div>
          </div>
        )}
      </Scenario>
      <ThinNote>
        {isIndustrial
          ? "IS pode incidir: há empresa marcada como indústria ou equiparada. Verifique os NCMs."
          : "IS não incluído: incide na 1ª comercialização, pelo fabricante ou importador."}
      </ThinNote>
    </Widget>
  );
}

function RegimeCard({ title, sub, regime, future, delay, isIndustrial }) {
  return (
    <Widget $span={2} $delay={delay} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">{title}</div>
      <div className="w-sub">{sub}</div>
      <TopHeadline>
        <span className="n" title={formatCurrency(regime.total)}><Num value={regime.total} format={formatCompact} /></span>
        <span className="l">de carga líquida, <Num value={regime.rate} format={formatPercent} /> do faturamento</span>
      </TopHeadline>
      <RegimeHead aria-hidden="true">
        <span>Tributo</span>
        <span>Débito coberto</span>
        <span>Débito</span>
        <span>Créditos</span>
        <span>Líquido</span>
      </RegimeHead>
      <RegimeBody>
      {regime.rows.map((r) => (
        <RegimeRow key={r.key}>
          <div className="name">
            {r.label}
            <small>
              {future
                ? <>COGS <Num value={r.creditCogs} format={formatCompact} /> · despesas <Num value={r.creditExpenses} format={formatCompact} /></>
                : r.note || <><Num value={r.coverage} format={formatPercent} /> coberto</>}
            </small>
          </div>
          <div className="bar" role="img" aria-label={`${formatPercent(r.coverage)} do débito coberto por crédito`}>
            {r.segments.map((s) => (
              <span key={s.key} style={{ width: `${Math.min(100, s.share)}%`, background: s.kind === "extra" ? IOS.mint : IOS.green, opacity: s.kind === "extra" ? 1 : 0.8 }} />
            ))}
          </div>
          <div className="n" title={formatCurrency(r.debit)}><Num value={r.debit} format={formatCompact} /></div>
          <div className="n" title={formatCurrency(r.credit)}><Num value={r.credit} format={formatCompact} /></div>
          <div className="net" title={formatCurrency(r.net)}><Num value={r.net} format={formatCompact} /></div>
        </RegimeRow>
      ))}
      <RegimeRow className="muted">
        <div className="name">
          {future && !isIndustrial && "IS: só indústria e importador recolhem"}
          {future && isIndustrial && "IS: indústria ou equiparada, verificar NCM"}
          {!future && !isIndustrial && "IS/IPI: só indústria e importador recolhem"}
          {!future && isIndustrial && "IS/IPI: indústria ou equiparada, verificar NCM"}
        </div>
        <div className="n">R$ 0</div>
        <div className="n">R$ 0</div>
        <div className="net">R$ 0</div>
      </RegimeRow>
      </RegimeBody>
    </Widget>
  );
}

function Sensitivity({ rows }) {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.delta)));
  const half = Math.ceil(rows.length / 2);
  const columns = [rows.slice(0, half), rows.slice(half)];
  return (
    <Widget $delay={350}>
      <div className="w-title">O que mais move o resultado</div>
      <div className="w-sub">Efeito de 1 ponto a mais em cada premissa sobre a diferença entre os regimes</div>
      <TornadoGrid>
        {columns.map((list, i) => (
          <div key={i}>
            <PlotHead aria-hidden="true">
              <span>Premissa</span>
              <span className="dir"><span>menos imposto</span><span>mais imposto</span></span>
              <span className="end">+1 ponto</span>
            </PlotHead>
            {list.map((r) => {
              const ratio = Math.abs(r.delta) / max;
              const color = directionColor(r.delta);
              const up = r.delta >= 0;
              const strength = Math.round((0.28 + ratio * 0.72) * 255).toString(16).padStart(2, "0");
              return (
                <PlotRow key={r.key}>
                  <div className="name">{SHORT_LABELS[r.key]}<small>Hoje em <Num value={r.current} format={formatPercent} /></small></div>
                  <div className="plot">
                    <i
                      className={up ? "up" : "down"}
                      style={{
                        width: `${ratio * 50}%`,
                        left: up ? "50%" : `${50 - ratio * 50}%`,
                        background: `linear-gradient(to ${up ? "right" : "left"}, ${color}33, ${color}${strength})`,
                      }}
                    />
                  </div>
                  <div className="num" title={formatCurrency(r.delta)}><Num value={r.delta} format={signed} /></div>
                </PlotRow>
              );
            })}
          </div>
        ))}
      </TornadoGrid>
    </Widget>
  );
}

export default function SimulationDashboard({ fiscalData, isIndustrial, companyFiltered }) {
  const { params, setParam, resetParams } = useReformaParams();
  const data = useMemo(
    () => buildSimulation(fiscalData, params, DEFAULT_PARAMS, PARAM_META),
    [fiscalData, params],
  );
  const { impact, margin, flow } = data;
  const changed = Math.abs(impact.fromReference) >= 1;
  const creditShare = flow.total > 0 ? ((flow.costTotal + flow.expenses) / flow.total) * 100 : 0;

  return (
    <>
      <Section $first>
        {companyFiltered && (
          <Notice style={{ marginTop: 0, marginBottom: 12 }}>
            <FiInfo />
            <span>Com empresa selecionada, a simulação mistura bases: PIS/COFINS é apurado na matriz, e as despesas (M105) são do projeto inteiro.</span>
          </Notice>
        )}
        <WidgetGrid>
          <Widget $delay={0}>
            <div className="w-head">{dot(directionColor(impact.value))} Variação absoluta</div>
            <div className="w-value"><MoneyValue value={impact.value} /></div>
            <div className="w-foot">{changed ? <>Referência: <Num value={impact.reference} format={signed} /></> : "Futuro − Atual, no período"}</div>
          </Widget>
          <Widget $delay={50}>
            <div className="w-head">{dot(directionColor(impact.value))} Variação percentual</div>
            <div className="w-value">{impact.percent.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</div>
            <div className="w-foot">Sobre a carga tributária atual</div>
          </Widget>
          <Widget $delay={100}>
            <div className="w-head">{dot(IOS.green)} Crédito adicional</div>
            <div className="w-value"><MoneyValue value={impact.extraCredit} /></div>
            <div className="w-foot">Despesas, vs. PIS/COFINS atual</div>
          </Widget>
          <Widget $delay={150}>
            <div className="w-head">{dot(IOS.red)} Margem tributável</div>
            <div className="w-value"><MoneyValue value={margin.value} /></div>
            <div className="w-foot"><Num value={margin.share} format={formatPercent} /> do faturamento</div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <Widget $span={3} $delay={150} style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div className="w-title">Da receita à margem tributável</div>
                <div className="w-sub">Compras e despesas geram crédito. Só o que sobra é tributado por CBS e IBS</div>
              </div>
              <Legend>
                <Tag $color={IOS.green}>Gera crédito</Tag>
                <Tag $color={IOS.red}>É tributado</Tag>
              </Legend>
            </div>
            <MarginFlow flow={flow} />
            <PeriodStats style={{ marginTop: 14 }}>
              <div>
                <span className="v" title={formatCurrency(flow.total)}><Num value={flow.total} format={formatCompact} /></span>
                <span className="l">Faturamento bruto total</span>
              </div>
              <div>
                <span className="v" title={formatCurrency(flow.costTotal)}><Num value={flow.costTotal} format={formatCompact} /></span>
                <span className="l">COGS total creditável no futuro</span>
              </div>
              <div>
                <span className="v"><Num value={creditShare} format={formatPercent} /></span>
                <span className="l">Da receita vira crédito</span>
              </div>
            </PeriodStats>
          </Widget>
          <SimulatorPanel params={params} setParam={setParam} resetParams={resetParams} impact={impact} isIndustrial={isIndustrial} />
        </WidgetGrid>
      </Section>

      <Section $delay={140}>
        <WidgetGrid>
          <RegimeCard title="Regime atual" sub="PIS, COFINS, ICMS e ISS" regime={data.current} delay={250} isIndustrial={isIndustrial} />
          <RegimeCard title="Regime futuro pleno" sub={<>CBS <Num value={Number(params.cbsPlena2033) || 0} format={formatPercent} /> + IBS SP <Num value={Number(params.ibsSpPlena2033) || 0} format={formatPercent} />, sem IS</>} regime={data.future} future delay={300} isIndustrial={isIndustrial} />
        </WidgetGrid>
      </Section>

      <Section $delay={180}>
        <Sensitivity rows={data.sensitivity} />
      </Section>
    </>
  );
}
